import { updateProject } from "@/components/common/project-provider/update-project";
import { AppDispatch } from "@/store/store";
import { GUID } from "@faro-lotv/foundation";
import {
  IElementGenericImgSheet,
  IElementSection,
} from "@faro-lotv/ielement-types";
import { addAreaDataSets } from "@faro-lotv/project-source";
import {
  createMutationSetAreaElevation,
  ProjectApi,
} from "@faro-lotv/service-wires";

/**
 * @returns the current elevation (in meters) shared by an area's sheets, or undefined when unknown.
 *
 * The sheets carry the real value in their own global pose; the area section's stays 0 until the
 * first elevation is set, so it's preferable to use a sheet and only fall back to the area.
 * @param sheet A sheet of the area (any sheet, since all sheets in an area share one elevation)
 * @param activeArea The area the sheets belong to
 */
export function getSheetsElevation(
  sheet?: IElementGenericImgSheet,
  activeArea?: IElementSection,
): number | undefined {
  return sheet?.globalPose?.pos.y ?? activeArea?.globalPose?.pos.y;
}

/** Dependencies and inputs needed to persist and locally apply a new sheets elevation. */
type ApplySheetsElevationParams = {
  /** The project API client used to send the mutation, re-query the area, and refetch it */
  projectApiClient: ProjectApi;
  /** The app dispatch, to refresh the local project */
  dispatch: AppDispatch;
  /** The area whose sheets elevation is being set */
  areaId: GUID;
  /** The new elevation, in meters */
  elevation: number;
  /** Aborts the volume query if the caller is torn down */
  signal: AbortSignal;
};

/**
 * Persist a new sheets elevation for an area and refresh the local project so the scene matches.
 *
 * The mutation moves the area, its sheets, and the area-dataset alignment that places the
 * capture-tree clouds (a sibling of the area, not a child), so it refetches the area subtree and
 * re-run the volume query for that alignment pose. The clouds' own subtrees are unchanged.
 */
export async function applySheetsElevation({
  projectApiClient,
  dispatch,
  areaId,
  elevation,
  signal,
}: ApplySheetsElevationParams): Promise<void> {
  await projectApiClient.applyMutations([
    createMutationSetAreaElevation({ elementId: areaId, elevation }),
  ]);

  const [dataSets] = await Promise.all([
    projectApiClient.queryAreaVolume(areaId, signal),
    dispatch(
      updateProject({
        projectApi: projectApiClient,
        iElementQuery: { ancestorIds: [areaId] },
      }),
    ),
  ]);
  dispatch(addAreaDataSets({ areaId, dataSets }));
}
